import { queryKeys } from '../keys'
import { DEFAULT_RETRY_MUTATION } from '../config'
import type { QueryClient } from '@tanstack/react-query'
import type {
  GetPresignResponse,
  UploadFromPresignProps,
} from '@/types/presign'
import type { GetUserProfileResponse } from '@/types/user'
import { presignService } from '@/services/presign'

const mutateGetPresign = (queryClient: QueryClient) => {
  const profileQueryKey = queryKeys.user.profile()

  return queryClient.setMutationDefaults<
    GetPresignResponse,
    unknown,
    UploadFromPresignProps,
    { previousProfile: GetUserProfileResponse }
  >(queryKeys.presign.upload(), {
    retry: DEFAULT_RETRY_MUTATION,
    mutationFn: async ({ file, filename }) => {
      const presign = await presignService.getPresignUrl({
        searchParams: { filename },
      })

      const previewUrl = await presignService.uploadWithPresign(presign, file)
      return { ...presign, previewUrl: previewUrl.previewUrl }
    },
    onMutate: async ({ file }) => {
      await queryClient.cancelQueries({ queryKey: profileQueryKey })

      const previousProfile = queryClient.getQueryData(
        profileQueryKey,
      ) as GetUserProfileResponse

      const newAvatar = URL.createObjectURL(file)

      queryClient.setQueryData<GetUserProfileResponse>(
        profileQueryKey,
        (old) =>
          !old
            ? old
            : {
                ...old,
                data: {
                  ...old.data,
                  admin: {
                    ...old.data.admin,
                    avatar: newAvatar,
                  },
                },
              },
      )

      return { previousProfile }
    },
    onError: (_err, _newData, context) => {
      if (context?.previousProfile) {
        queryClient.setQueryData(profileQueryKey, context.previousProfile)
      }
    },
  })
}

const mutateUploads = (queryClient: QueryClient) => {
  return queryClient.setMutationDefaults(queryKeys.presign.uploads(), {
    retry: DEFAULT_RETRY_MUTATION,
    mutationFn: (params: Parameters<typeof presignService.uploads>[0]) =>
      presignService.uploads(params),
  })
}

export function registerPresignMutations(queryClient: QueryClient) {
  mutateGetPresign(queryClient)
  mutateUploads(queryClient)
}
